import { useState } from "react";
import { Box, Card, CircularProgress, Typography } from "@mui/material";
import { api } from "../services/api";
import { useApiData } from "../hooks/useApiData";
import { useSession } from "../context/SessionContext";
import { DataState } from "../components/Feedback";
import PageHeader from "../components/PageHeader";
import SectionCard from "../components/SectionCard";
import DataTable, { TwoLine } from "../components/DataTable";
import StatusChip from "../components/StatusChip";
import { CONTROL } from "../constants";
import { complianceColor } from "../utils/colors";
import { formatDate } from "../utils/format";

function FrameworkCard({ framework, selected, onSelect }) {
  const color = complianceColor(framework.compliancePct);
  return (
    <Card
      onClick={onSelect}
      sx={{
        p: 2.5,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: 2.5,
        outline: selected ? "2px solid #4F46E5" : "2px solid transparent",
        transition: "transform .18s ease, box-shadow .18s ease",
        "&:hover": { transform: "translateY(-3px)", boxShadow: "0 12px 32px rgba(15,23,42,0.12)" },
      }}
    >
      <Box sx={{ position: "relative", display: "inline-flex", flexShrink: 0 }}>
        <CircularProgress variant="determinate" value={100} size={72} thickness={5} sx={{ color: "rgba(15,23,42,0.07)" }} />
        <CircularProgress
          variant="determinate"
          value={framework.compliancePct}
          size={72}
          thickness={5}
          sx={{ color, position: "absolute", left: 0, "& .MuiCircularProgress-circle": { strokeLinecap: "round" } }}
        />
        <Box sx={{ position: "absolute", inset: 0, display: "grid", placeItems: "center" }}>
          <Typography sx={{ fontWeight: 800, fontSize: 17 }}>{framework.compliancePct}%</Typography>
        </Box>
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700 }}>{framework.name}</Typography>
        <Typography variant="caption" color="text.secondary">
          {framework.totalControls} controls · {framework.implemented} implemented · {framework.partiallyImplemented} partial · {framework.notImplemented} open
        </Typography>
      </Box>
    </Card>
  );
}

export default function FrameworksPage() {
  const { clientId } = useSession();
  const [frameworkId, setFrameworkId] = useState(null);
  const frameworks = useApiData(() => api.getFrameworks(), [clientId]);
  const controls = useApiData(() => api.getControls(frameworkId), [clientId, frameworkId]);

  const columns = [
    { key: "controlRef", label: "Control", width: 150, render: (c) => <TwoLine main={c.controlRef} sub={c.frameworkCode} /> },
    { key: "title", label: "Title" },
    { key: "owner", label: "Owner" },
    {
      key: "status", label: "Status",
      render: (c) => <StatusChip label={c.statusLabel} color={CONTROL[c.status].color} />,
    },
    { key: "evidenceCount", label: "Evidence", align: "right" },
    { key: "lastReviewed", label: "Last reviewed", render: (c) => formatDate(c.lastReviewed) },
  ];

  return (
    <>
      <PageHeader
        title="Frameworks & Controls"
        subtitle="How each framework is doing. Select a framework to focus the control list."
      />
      <DataState state={frameworks}>
        {(list) => (
          <Box sx={{ display: "grid", gap: 3 }}>
            <Box sx={{ display: "grid", gap: 2.5, gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)", xl: "repeat(3, 1fr)" } }}>
              {list.map((f) => (
                <FrameworkCard
                  key={f.id}
                  framework={f}
                  selected={frameworkId === f.id}
                  onSelect={() => setFrameworkId(frameworkId === f.id ? null : f.id)}
                />
              ))}
            </Box>
            <SectionCard
              title={frameworkId ? list.find((f) => f.id === frameworkId)?.name : "All controls"}
              subtitle={frameworkId ? "Click the framework again to show every control" : "Showing controls from every framework"}
            >
              <DataState state={controls}>
                {(rows) => <DataTable columns={columns} rows={rows} emptyText="No controls yet." />}
              </DataState>
            </SectionCard>
          </Box>
        )}
      </DataState>
    </>
  );
}
